import AddTool, { AddToolContext } from "./add-tool";
import SectionView from "../../../views/section-view";
import Signal from "../../../control-flow/signal";
import SlantedShelfView from "../../../views/slanted-shelf-view";
import logging from '../../../logging.js';
import { Group, SlantedShelf } from "../../../domain";
import { PI } from "../../../math/constants";
import { clamp } from "three/src/math/MathUtils";
import { getParentViewOrNull, getParentView } from "../../../controllers/base-view-controllers";
import { overlapsSectionContent } from "../../../view3d/overlaps-section-view";
import { slotAbove, slotBelow, round2slot } from "../../../measurement";
import { getSlantedShelfHeight } from "../../../controllers/slanted-shelf-controllers";

const log = logging('tools');

export class AddSlantedShelfTool extends AddTool {
    target?: SectionView;

    model: SlantedShelf;
    proxy: SlantedShelfView;

    declare onSuccess: Signal<[SlantedShelf]>;

    constructor(shelf: SlantedShelf, icon: HTMLElement, ctx: AddToolContext) {
        super(icon, ctx);

        this.model = SlantedShelf.fromJSON({
            width: 18,
            thickness: shelf.thickness,
            depth: 14,

            fenceAssetId: shelf.fenceAssetId,
        });
        this.ctx.defaults.reset(this.model);

        const group = Group.fromJSON({ rotation: { x: PI/10, y: PI/20, z: 0 }});
        group.slantedShelves.push(this.model);
        group.commit(true);

        this.updateThumbnail(group);
    }

    dragenter() {
        log.debug('AddSlantedShelfTool.dragenter()');
        this.proxy = new SlantedShelfView(this.model);
    }

    dragover(event: PointerEvent, target: HTMLElement) {
        log.debug('AddSlantedShelfTool.dragover()');

        this.updateWorldPosition(event, target);
        this.onDisplayChange.trigger();

        const
            y = this.worldPosition.y,
            height = getSlantedShelfHeight(this.model);

        const
            hit = this.ctx.picker.topHit,
            view = hit ? getParentViewOrNull(hit) as SectionView : null;
        if(hit?.userData.isSectionHit
           && !overlapsSectionContent(view, y, height, this.proxy))
        {

            // Preventing the default here indicates that this is a valid drop
            // zone. Without doing this, the browser does not give us a `drop`
            // event, only a dragend().
            event.preventDefault();
            const
                target: SectionView = this.target = getParentView(hit) as SectionView,
                min = slotAbove(target.model.position.y),
                max = slotBelow(target.model.position.y + target.model.height),
                slot = clamp(round2slot(this.worldPosition.y), min, max);

            target.add(this.proxy);

            this.model.position.x = 0;
            this.model.position.y = slot;
            this.model.position.z = 25;
            this.model.width = target.model.width;
        }
        else {
            delete this.target;

            // check if the proxy belongs to the scene ... if so, fine, else
            // remake the proxy and add it back to the scene.
            if(this.proxy.parent != this.ctx.scene) {
                this.proxy.removeFromParent();
                this.ctx.scene.add(this.proxy);
            }

            this.model.position.x = this.worldPosition.x - 6;
            this.model.position.y = this.worldPosition.y;
            this.model.position.z = 25;
            this.model.width = 24;
        }
    }

    dragleave() {
        log.debug('AddSlantedShelfTool.dragleave()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    cancel() {
        log.debug('AddSlantedShelfTool.cancel()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    end() {
        log.debug('AddSlantedShelfTool.end()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();

        // if we ended over a section, then we'll have a defined target
        // property, which means we can drop something.
        if(this.target) {
            const model = this.model.clone(true);
            model.parent = this.target.model;
            model.position.z = 0;
            this.target.model.slantedShelves.push(model);

            this.onSuccess.trigger(model);
        }
    }

}
export default AddSlantedShelfTool;
